import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatTime, t } from '@aquabox/shared';
import { Icon, type IconName } from './Icon';
import { NotificationBell } from './NotificationBell';
import { NewOrderWatcher } from './NewOrderWatcher';
import type { Channel, Dashboard, TenantState } from '../api/types';
import { useMe, useUnmappedCount, useApprovalCount } from '../api/queries';
import { api } from '../api/client';
import { usePrintQueue } from '../api/print-queue';
import { printBadgeText } from '../lib/print-queue';

/**
 * Sidebar navigation items (§2.2).
 *
 * `navItems` returns the full list including pending-order badge.  Badge content
 * is driven by `dashboard.today.pending` and unmapped integration count.
 *
 * Daraltma davranışı: `aquabox.sidebar` anahtarı `localStorage`'da `collapsed` |
 * `expanded` değerini tutar.  Buna göre `.app-shell[data-sidebar]` özniteliği
 * set edilir ve CSS bu özniteliğe göre sidebar genişliğini, nav-label
 * görünürlüğünü ve ikon modunu yönetir.
 */

/**
 * Gün Tahtası menüden ve rotadan gizli (UX, 31.10); kod duruyor. `VITE_DAYBOARD=1` ile yeniden açılır.
 */
function dayBoardEnabled(): boolean {
  return import.meta.env.VITE_DAYBOARD === "1";
}

const SIDEBAR_KEY = 'aquabox.sidebar';

// `window.matchMedia` SSR/jest ortamında olmayabilir; yardımcı tek noktada
// korur, toggleSidebar ve render tarafı aynı kapıyı kullanır.
const mq = (q: string) => typeof window.matchMedia === 'function' && window.matchMedia(q).matches;

interface NavItem {
  to: string;
  labelKey: Parameters<typeof t>[0];
  icon: IconName;
  badge?: string;
  disabled?: boolean;
}

interface NavGroup {
  labelKey: Parameters<typeof t>[0];
  items: NavItem[];
}

/** Menü grupları: İş · Kişiler · Para · Ayarlar. Boş kalan grup render'da çizilmez. */
function navGroups(
  dashboard: Dashboard | undefined,
  unmappedCount: number,
  approvalCount: number,
  printQueue: { visible: boolean; unprinted: number; truncated: boolean },
  canPersonnel: boolean,
  canDepot: boolean,
  canApprovals: boolean,
): NavGroup[] {
  const pending = dashboard?.today.pending ?? 0;
  return [
    {
      labelKey: 'nav.group.work',
      items: [
        { to: '/', labelKey: 'nav.overview', icon: 'grid' },
        {
          to: '/siparisler',
          labelKey: 'nav.orders',
          icon: 'list',
          badge: pending > 0 ? String(pending) : undefined,
        },
        ...(dayBoardEnabled()
          ? [{ to: '/gun-tahtasi', labelKey: 'nav.dayBoard' as const, icon: 'grid' as const }]
          : []),
        { to: '/panel-atama', labelKey: 'nav.panelAssign', icon: 'link' },
        { to: '/seferler', labelKey: 'nav.trips', icon: 'van' },
        { to: '/harita', labelKey: 'nav.map', icon: 'pin' },
        ...(canDepot
          ? [{ to: '/depo', labelKey: 'nav.depot' as const, icon: 'drop' as const }]
          : []),
        { to: '/stok', labelKey: 'nav.stock', icon: 'box' },
        // Onaylar ucu yalnız OWNER/MANAGER'a açık (`/api/approvals`); diğer rollerde
        // menüde de görünmez (rota da gizlidir).
        ...(canApprovals
          ? [
              {
                to: '/onaylar',
                labelKey: 'panel.approvals.nav' as const,
                icon: 'check' as const,
                badge: approvalCount > 0 ? String(approvalCount) : undefined,
              },
            ]
          : []),
        ...(printQueue.visible
          ? [
              {
                to: '/yazdir',
                labelKey: 'panel.print.navQueue' as const,
                icon: 'print' as const,
                badge: printBadgeText(printQueue.unprinted, printQueue.truncated),
              },
            ]
          : []),
      ],
    },
    {
      labelKey: 'nav.group.people',
      items: [
        ...(canPersonnel
          ? [{ to: '/personel', labelKey: 'nav.personnel' as const, icon: 'users' as const }]
          : []),
        { to: '/musteriler', labelKey: 'nav.customers', icon: 'users' },
        { to: '/araclar', labelKey: 'nav.vehicles', icon: 'car' },
      ],
    },
    {
      labelKey: 'nav.group.money',
      items: [
        { to: '/kasa', labelKey: 'nav.accounting', icon: 'wallet' },
        { to: '/raporlar', labelKey: 'nav.reports', icon: 'chart' },
      ],
    },
    {
      labelKey: 'nav.group.settings',
      items: [
        { to: '/ayarlar', labelKey: 'nav.settings', icon: 'sliders' },
        {
          to: '/entegrasyonlar',
          labelKey: 'nav.integrations',
          icon: 'link',
          badge: unmappedCount > 0 ? String(unmappedCount) : undefined,
        },
        { to: '/urunler', labelKey: 'nav.products', icon: 'drop' },
        { to: '/bolgeler', labelKey: 'nav.regions', icon: 'pin' },
        { to: '/kurulum', labelKey: 'nav.setup', icon: 'check' },
      ],
    },
  ];
}

const TITLES: Record<string, Parameters<typeof t>[0]> = {
  '/': 'nav.overview',
  '/siparisler': 'nav.orders',
  '/gun-tahtasi': 'nav.dayBoard',
  '/yazdir': 'panel.print.navQueue',
  '/panel-atama': 'nav.panelAssign',
  '/musteriler': 'nav.customers',
  '/seferler': 'nav.trips',
  '/kasa': 'nav.accounting',
  '/stok': 'nav.stock',
  '/onaylar': 'panel.approvals.nav',
  '/depo': 'nav.depot',
  '/harita': 'nav.map',
  '/personel': 'nav.personnel',
  '/urunler': 'nav.products',
  '/araclar': 'nav.vehicles',
  '/bolgeler': 'nav.regions',
  '/entegrasyonlar': 'nav.integrations',
  '/raporlar': 'nav.reports',
  '/ayarlar': 'nav.settings',
  '/kurulum': 'nav.setup',
};

/**
 * Başlık çubuğundaki bildirim zili (§2.3).
 *
 * Bildirim paneli sunucu `/api/notifications` üzerinden okunur ve okundu
 * işaretleme isteği doğrudan sunucuya gider — yerel state'e yazılmaz.
 *
 * Panel dışarı tıklandığında veya Escape tuşuna basıldığında kapanır.
 * Kapanma odağı zil düğmesine döndürür.
 */
export function Layout({
  children,
  dashboard,
  tenant,
  onLogout,
  userName,
  canPrint = false,
  canPersonnel = false,
  canDepot = false,
}: {
  children: React.ReactNode;
  dashboard: Dashboard | undefined;
  tenant: Pick<TenantState, 'name' | 'status'> | undefined;
  onLogout: () => void;
  userName: string;
  /** Yazdırma kuyruğu menüsü ve rozeti yalnız OWNER/MANAGER için (App.tsx). */
  canPrint?: boolean;
  /** "Personel" menü öğesi yalnız OWNER/MANAGER için (App.tsx). */
  canPersonnel?: boolean;
  /** "Depo günü" menü öğesi yalnız OWNER/MANAGER için (App.tsx). */
  canDepot?: boolean;
}) {
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const printQueue = usePrintQueue(canPrint);
  const [sidebarState, setSidebarState] = useState<'expanded' | 'collapsed' | null>(() => {
    try {
      const val = localStorage.getItem(SIDEBAR_KEY);
      if (val === 'collapsed' || val === 'expanded') return val;
      return null;
    } catch {
      return 'expanded';
    }
  });
  const unmapped = useUnmappedCount();
  const me = useMe();
  const isManager = me.data?.role === 'OWNER' || me.data?.role === 'MANAGER';
  // `/api/approvals/count` yalnız OWNER/MANAGER'a açık — DRIVER'a 403 döner. Sorgu rol kapısıyla
  // birlikte açılır; rozet zaten yalnız yöneticilere çizilir.
  const approvals = useApprovalCount(isManager);

  /**
   * Elle bağlanan kanal oturumu şeridi (Getir — ADR 2026-10-10).
   *
   * Oturumun süresi dolmak üzere (`EXPIRING`) ya da dolmuş (`UNBOUND`) ise TÜM sayfalarda
   * üstte kalıcı şerit görünür. **Kapatma düğmesi YOK**: sessizce kapanan bir uyarı uyarı
   * değildir; bayi oturumu yenileyene kadar hatırlatma kalmalıdır.
   *
   * Veri YENİ uçtan gelmez — mevcut `/api/channels` listesi okunur (`queryKey: ['channels']`
   * entegrasyonlar ekranıyla aynı önbellek girdisi). Yalnız OWNER/MANAGER görür; bu roller
   * dışında kanal ucu da onlara açıktır, o yüzden sorgu `enabled` ile kapılır.
   */
  const channels = useQuery({
    queryKey: ['channels'],
    queryFn: () => api.get<Channel[]>('/api/channels'),
    enabled: isManager,
  });
  const sessionWarning = (channels.data ?? [])
    .filter((c) => c.session && c.session.state !== 'BOUND' && c.status !== 'DISABLED')
    .map((c) =>
      c.session?.state === 'EXPIRING' && c.session.expiresAt
        ? t('panel.channelSession.expiring', {
            source: t(`channelSession.source.${c.source}`),
            time: formatTime(c.session.expiresAt),
          })
        : t('panel.channelSession.unbound', { source: t(`channelSession.source.${c.source}`) }),
    );
  const title = t(TITLES[pathname] ?? 'nav.overview');
  const initials = (tenant?.name ?? 'AquaBox')
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toLocaleUpperCase('tr-TR') ?? '')
    .join('');

  // Telefon çekmecesi rota değişince kapanır.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  const toggleSidebar = () => {
    const isPhone = mq('(max-width: 639px)');
    if (isPhone) {
      setDrawerOpen((v) => !v);
    } else {
      const effectiveCollapsed =
        sidebarState === 'collapsed' || (sidebarState === null && mq('(max-width: 1023px)'));
      const next = effectiveCollapsed ? 'expanded' : 'collapsed';
      setSidebarState(next);
      try {
        localStorage.setItem(SIDEBAR_KEY, next);
      } catch {
        // localStorage erişimi başarısız olursa sessizce devam et.
      }
    }
  };

  const isPhone = mq('(max-width: 639px)');
  const effectiveCollapsed =
    sidebarState === 'collapsed' || (sidebarState === null && mq('(max-width: 1023px)'));
  const ariaLabel = isPhone
    ? t('panel.toggleMenu')
    : effectiveCollapsed
      ? t('panel.expandMenu')
      : t('panel.collapseMenu');

  return (
    <div className="app-shell" data-sidebar={sidebarState ?? undefined}>
      <div
        aria-hidden="true"
        className={`sidebar-backdrop${drawerOpen ? ' open' : ''}`}
        onClick={() => setDrawerOpen(false)}
      />
      <aside className={`sidebar${drawerOpen ? ' open' : ''}`}>
        <div className="sidebar-brand">
          <span className="sidebar-brand-mark" aria-hidden="true">
            AB
          </span>
          <span className="sidebar-brand-text">AquaBox</span>
        </div>
        <nav style={{ flex: 1 }}>
          {navGroups(
            dashboard,
            unmapped.data?.count ?? 0,
            approvals.data?.total ?? 0,
            {
              visible: canPrint,
              unprinted: printQueue.unprintedCount,
              truncated: printQueue.truncated,
            },
            canPersonnel,
            canDepot,
            isManager,
          )
            .filter((group) => group.items.length > 0)
            .map((group) => (
              <div key={group.labelKey}>
                <div
                  className="nav-label nav-group-title"
                  style={{
                    padding: '12px 12px 4px',
                    fontSize: 11.5,
                    fontWeight: 600,
                    opacity: 0.6,
                  }}
                >
                  {t(group.labelKey)}
                </div>
                {group.items.map((item) =>
                  item.disabled ? (
                    <span key={item.to} title={t('panel.comingSoon')} className="nav-link-disabled">
                      <Icon name={item.icon} />
                      <span className="nav-label">{t(item.labelKey)}</span>
                    </span>
                  ) : (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === '/'}
                      title={t(item.labelKey)}
                      className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                    >
                      <Icon name={item.icon} />
                      <span className="nav-label">{t(item.labelKey)}</span>
                      {item.badge && <span className="badge-amber">{item.badge}</span>}
                    </NavLink>
                  ),
                )}
              </div>
            ))}
        </nav>
        <div className="sidebar-footer">
          <span className="sidebar-avatar" aria-hidden="true">
            {initials}
          </span>
          <span className="sidebar-tenant-name">{tenant?.name ?? '—'}</span>
          <button onClick={onLogout} className="sidebar-logout" title={t('nav.logout')}>
            <Icon name="arrow-right" />
            <span className="nav-label">{t('nav.logout')}</span>
          </button>
        </div>
      </aside>

      <div className="main-col">
        {sessionWarning.length > 0 && (
          <div
            role="status"
            className="channel-session-strip"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '8px 16px',
              background: '#fff4e5',
              borderBottom: '1px solid #f0c896',
              color: '#7a4b00',
              fontSize: 13,
            }}
          >
            <span>{sessionWarning.join(' · ')}</span>
            <NavLink to="/entegrasyonlar" style={{ color: '#7a4b00', fontWeight: 600 }}>
              {t('panel.channelSession.relogin')}
            </NavLink>
          </div>
        )}
        <header className="main-header">
          <button
            type="button"
            className="sidebar-toggle"
            aria-label={ariaLabel}
            aria-expanded={isPhone ? drawerOpen : !effectiveCollapsed}
            onClick={toggleSidebar}
          >
            <Icon name="menu" />
          </button>
          <h1 className="h1">{title}</h1>
          <span className="header-date">{todayLabel()}</span>
          <span style={{ flex: 1 }} />
          <NewOrderWatcher />
          <NotificationBell />
          <span className="header-username">{userName}</span>
        </header>
        <main className="main-content">{children}</main>
      </div>
    </div>
  );
}

/** "19 Ağustos 2026, Salı" (§2.3). Türkiye saatiyle gösterilir. */
function todayLabel(): string {
  return new Intl.DateTimeFormat('tr-TR', {
    timeZone: 'Europe/Istanbul',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    weekday: 'long',
  }).format(new Date());
}
